// packages/api/src/hooks/usePrivacy.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  MyDataResponse,
  Consent,
  ConsentUpdatePayload,
  DeletionRequestResponse,
  DeletionRequest,
  DeletionRequestDetail,
  DeletionRequestsQueryParams,
  PaginatedResponse,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

/**
 * Fetch user's personal data (LGPD).
 */
export function useMyData() {
  return useQuery({
    queryKey: queryKeys.privacy.myData,
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<MyDataResponse>>('/privacy/my-data');
      return response.data.data;
    },
  });
}

/**
 * Fetch user's consents.
 */
export function useConsents() {
  return useQuery({
    queryKey: queryKeys.privacy.consents,
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<{ consents: Consent[] }>>(
        '/privacy/consent',
      );
      return response.data.data.consents;
    },
  });
}

/**
 * Mutation to update a consent.
 */
export function useUpdateConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ConsentUpdatePayload) => {
      const response = await apiClient.post<ApiSuccess<Consent>>('/privacy/consent', payload);
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.privacy.consents });
    },
  });
}

/**
 * Mutation to request data deletion (user-initiated).
 * Renamed from `useDeletionRequest` -> `useRequestDeletion` to avoid
 * collision with the admin-side detail query hook.
 */
export function useRequestDeletion() {
  return useMutation({
    mutationFn: async (password: string) => {
      const response = await apiClient.post<ApiSuccess<DeletionRequestResponse>>(
        '/privacy/deletion-request',
        { password },
      );
      return response.data.data;
    },
  });
}

// ---------------------------------------------------------------------------
// Admin: Deletion Requests (LGPD management)
// ---------------------------------------------------------------------------

/**
 * List deletion requests with optional filters (admin).
 * Maps backend `{ count, rows }` shape to the shared `PaginatedResponse<T>`.
 */
export function useDeletionRequests(filters?: DeletionRequestsQueryParams) {
  return useQuery({
    queryKey: queryKeys.privacy.adminDeletionRequests.list(
      filters as Record<string, unknown> | undefined,
    ),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiSuccess<{ count: number; rows: DeletionRequest[] }>
      >('/privacy/admin/deletion-requests', { params: filters });
      const raw = response.data.data;
      const limit = filters?.limit ?? 20;
      const page = filters?.page ?? 1;
      return {
        items: raw.rows,
        total: raw.count,
        page,
        limit,
        totalPages: limit > 0 ? Math.ceil(raw.count / limit) : 0,
      } satisfies PaginatedResponse<DeletionRequest>;
    },
  });
}

/**
 * Fetch a single deletion request with user summary + 21-day deadline (admin).
 */
export function useDeletionRequest(id: string) {
  return useQuery({
    queryKey: queryKeys.privacy.adminDeletionRequests.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<DeletionRequestDetail>>(
        `/privacy/admin/deletion-requests/${id}`,
      );
      return response.data.data;
    },
    enabled: !!id,
  });
}

/**
 * Approve a pending deletion request (admin).
 */
export function useApproveDeletion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.put<ApiSuccess<DeletionRequest>>(
        `/privacy/admin/deletion-requests/${id}/approve`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.privacy.adminDeletionRequests.all,
      });
    },
  });
}

/**
 * Reject a pending deletion request (admin).
 * Backend enforces @MinLength(10) on `reason` via RejectDeletionDto.
 */
export function useRejectDeletion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await apiClient.put<ApiSuccess<DeletionRequest>>(
        `/privacy/admin/deletion-requests/${id}/reject`,
        { reason },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.privacy.adminDeletionRequests.all,
      });
    },
  });
}
